import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CANVAS_TAPE_ROLL_ON_MS, canvasTapeAngles, getCanvasTape, readCanvasTape, stepCanvasTapeAngle } from './tapes/canvasTapes'
import { useRotateDrag } from './hooks/useRotateDrag'
import { canvasRotateCursorAt } from './cursors/canvasRotateCursors'
import './CanvasTape.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

export interface CanvasTapeProps {
  /** Der Zettel, auf dem er klebt — für den Klick nach oben. */
  noteId: string
  /**
   * Was in `canvasNotes.tape` steht: `muster:lage:winkel`, beim **Ankleben**
   * gewürfelt und seitdem unverändert.
   */
  tape: string
  /** Ob er gerade **angetippt** ist — dann steht seine Leiste offen. */
  tapped: boolean
  /** Ob er sich gerade **abzieht** — die Bewegung läuft, die Zeile klebt noch. */
  peeling: boolean
  /**
   * Meldet, dass das **Abziehen** durch ist — dann erst darf die Zeile fallen.
   *
   * Der Streifen sagt es selbst, statt dass der Canvas die Zeit abzählt: Eine
   * Uhr, die im Klick startet, läuft der Animation um einen Frame voraus (sie
   * beginnt erst, wenn der Stil steht), und der Streifen verschwand kurz vor
   * dem Ende seiner Bewegung.
   */
  onPeeled?: (noteId: string) => void
  /** Klick auf den Streifen: Er ist der **Griff** des geklebten Zettels. */
  onClick: (noteId: string) => void
  /**
   * **Neigung ziehen** — die Raste, auf der ein Zug geendet hat.
   *
   * Kommt nur, wenn sich die Stufe wirklich geändert hat: Wer wackelt und auf
   * derselben landet, hat geklickt.
   */
  onAngle?: (noteId: string, angle: number) => void
  /**
   * **Unter der Hand ist nichts greifbar** (CONTEXT.md, Werkzeug-Pille).
   *
   * Der Streifen ist der einzige Griff, den ein geklebter Zettel behält — und
   * er holt sich seine Pointer-Events zurück, die der Node längst abgegeben
   * hat (`.note-node--taped .canvas-tape { pointer-events: auto }`). Im
   * Hand-Werkzeug galt das bis September 2026 auch: Klick tippte an, Zug
   * stellte die Neigung, `Tab` fand ihn sowieso. Gesperrt ist er aus der
   * Tab-Reihenfolge, aus Klick und Taste heraus; die Pointer-Events nimmt
   * ihm die SCSS (`.canvas-flow--panning`), denn Chromium schickt sie auch an
   * gesperrte Knöpfe. Gemeldet 2026-09-06.
   */
  disabled?: boolean
}

/**
 * Der **Klebestreifen** eines geklebten Zettels — schräg über der linken oberen
 * Ecke, oder waagerecht mittig auf der Oberkante (siehe `getCanvasTapeSpot`).
 *
 * Er ist mehr als Zierde: **Er ist der Griff.** Die Fläche des Zettels nimmt
 * keine Pointer-Events mehr an (der Zeiger fällt durch ihn hindurch auf den
 * Canvas), der Streifen und das **Eselsohr** schon. Ein Klick auf ihn tippt
 * den Zettel an und öffnet die Leiste mit dem einen Knopf **Ablösen**.
 *
 * **Drei Lagen, eine Kante** — siehe `CanvasTape.scss` für das Warum:
 *
 * 1. die liegende Lage, beschnitten von `--peel`,
 * 2. die **Rolle** am laufenden Ende, um ihre Querachse gedreht,
 * 3. der Schatten darunter.
 *
 * Der Streifen ragt **frei** über die Kanten des Zettels hinaus, so wie im
 * Entwurf. Er kann dabei über einem Nachbarn liegen (und dessen Klicks
 * annehmen) oder unter einem jüngeren verschwinden — die Tiefe geht nach
 * Alter. Verdeckt ihn einer, bleibt **Tab** der Weg zum Ablösen.
 *
 * `nodrag` und `nopan`: React Flow soll aus einem Druck auf ihn weder ein
 * Verschieben noch ein Schieben der Ansicht machen.
 */
export const CanvasTape: React.FC<CanvasTapeProps> = ({ noteId, tape, tapped, peeling, onClick, onPeeled, onAngle, disabled = false }) => {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLButtonElement>(null)

  /*
    **Das Aufrollen gehört zum Ankleben, nicht zum Zeichnen.**

    Die Bewegung hing an der Basisklasse und lief damit, sooft der Browser den
    Streifen frisch aufbaute. Beim Ankleben ist das richtig — dort wird er
    eingehängt. Beim Wechsel auf einen Tab aber auch: Ein inaktiver Tab steht
    auf `display: none` (`TabStack.scss`), und ein Element, das daraus
    zurückkommt, startet seine CSS-Animationen von vorn. Wer einen Tab mit
    geklebten Zetteln öffnete, sah sie alle noch einmal aufrollen.

    Die Klasse verschwindet deshalb, sobald die Bewegung durch ist — was nicht
    mehr da ist, kann auch nicht neu anlaufen.

    Läuft gar keine Animation (`prefers-reduced-motion`, fehlendes `@property`),
    kommt kein `animationend` — dafür der Wecker. Er ist absichtlich etwas länger
    als die Bewegung: Er soll sie nicht abschneiden, sondern nur verhindern, dass
    die Klasse für immer stehen bleibt und den gemeldeten Fehler zurückbringt.

    Nicht über `getAnimations()`: Die Liste ist auch dann leer, wenn die
    Animation bloß noch nicht angelaufen ist, und der Streifen verlöre seine
    Bewegung schon beim Ankleben.
  */
  const [rollingOn, setRollingOn] = useState(true)

  useEffect(() => {
    if (!rollingOn) return undefined
    const wecker = setTimeout(() => setRollingOn(false), CANVAS_TAPE_ROLL_ON_MS + 80)
    return () => clearTimeout(wecker)
  }, [rollingOn])

  /*
    **Fertig ist, wenn die Bewegung fertig ist** — nicht, wenn eine Uhr
    abgelaufen ist.

    `getAnimations()` fragt das Element selbst; `finished` ist erfüllt, wenn der
    letzte Frame gezeichnet ist. Läuft gar keine Animation — bei
    `prefers-reduced-motion`, oder wo `@property` fehlt und die Länge nicht
    überblendet —, ist die Liste leer und die Meldung kommt sofort: Dort SOLL
    der Streifen ohne Bewegung verschwinden.

    Ein abgebrochenes `finished` (das Element geht vorher) lehnt ab; das darf
    still bleiben, denn dann ist der Streifen ohnehin aus dem Bild.
  */
  useEffect(() => {
    if (!peeling) return
    if (!onPeeled) return
    // `?.()` und nicht `?.`: Wo es die Web-Animations-API gar nicht gibt (jsdom,
    // sehr alte Engines), soll die Liste leer sein — nicht der Aufruf platzen.
    const laufend = rootRef.current?.getAnimations?.() ?? []
    if (laufend.length === 0) {
      onPeeled(noteId)
      return
    }
    let gilt = true
    void Promise.all(laufend.map(a => a.finished))
      .then(() => { if (gilt) onPeeled(noteId) })
      .catch(() => undefined)
    return () => { gilt = false }
  }, [peeling, noteId, onPeeled])

  /*
    ── **Neigung** ───────────────────────────────────────────────────────────

    Der Streifen ist zweierlei: der **Griff**, der antippt, und das Blatt, das
    sich drehen lässt. Welches von beidem gemeint war, entscheidet das ERGEBNIS
    und nicht die Strecke — wer wackelt und auf derselben Raste landet, hat
    geklickt. Andernfalls führte ein Zittern von fünf Pixeln zu einem Druck, der
    nichts tut.

    **Gegriffen wird auf der FLÄCHE, nicht an Ecken.** Der Zettel bekommt vier
    Zonen schräg außerhalb seiner Ecken (siehe `RotateHandles`); der Streifen
    nicht. Er ist 128×36 groß und liegt mit seinen Ecken auf dem Papier — Zonen
    dort wären kleiner als der Zeiger und lägen über dem Zettel, den man gerade
    nicht meint. **Er ist ohnehin schon der einzige Griff, den ein geklebter
    Zettel behält.** Die Geste hat sich im September 2026 geändert (senkrechter
    Zug → Kreis um die Mitte), nicht die Stelle, an der sie anfängt.

    **Das Vorzeichen dreht hier, an EINER Stelle.** Der Streifen zählt GEGEN den
    Uhrzeigersinn (`rotate(-1 * --tape-angle)` in der SCSS, damit sein freies
    Ende sich vom Blatt hebt); die Geste zählt mit ihm, wie CSS es täte. Die
    Zeile umzudrehen hieße, jede gespeicherte zu migrieren, um eine Zahl
    hübscher zu machen.
  */
  const look = readCanvasTape(tape)
  const shape = getCanvasTape(look)

  /*
    **Während des Abziehens nimmt er keine Geste mehr an.** Er ist auf dem Weg
    aus dem Bild; eine Neigung einzustellen wäre sinnlos.

    `disabled` zusätzlich zum Attribut: Pointer-Events erreichen in Chromium
    auch einen gesperrten Knopf, nur `click` und `keydown` nicht.
  */
  const stellbar = Boolean(onAngle) && !peeling && !disabled

  const rotate = useRotateDrag({
    getCenter: () => {
      // Der Streifen dreht um seine **Mitte** — und die ist auch bei einem
      // gedrehten Element der Mittelpunkt seines Umgebungsrechtecks.
      const box = rootRef.current?.getBoundingClientRect()
      return box ? { x: box.left + box.width / 2, y: box.top + box.height / 2 } : null
    },
    getRasten: () => canvasTapeAngles(look?.spot ?? 'corner').map(a => -a),
    getStart: () => -(look?.angle ?? 0),
    onCommit: angle => onAngle?.(noteId, -angle),
    enabled: stellbar,
  })
  // Der Doppelklick fällt weg: Der Streifen hat keine Null, auf die er
  // zurückkönnte — in der Ecke gibt es sie gar nicht (die Rasten fangen bei 7°
  // an). Zurück zum Zufall führt allein **Ablösen** und neu ankleben.
  const { onDoubleClick: _reset, onPointerMove: rotateMove, ...rotateHandlers } = rotate.handlers
  const { consumeDragged, rotating } = rotate

  /*
    ── **Der Cursor zeigt die Richtung, in die man greift** ─────────────────

    Der Streifen hat keine vier Zonen — gegriffen wird seine ganze Fläche. Die
    Ecke ergibt sich deshalb aus der Lage des ZEIGERS zur Drehmitte, genau wie
    sie sich am Zettel aus der Zone ergibt. Ein Cursor, der links unten
    dasselbe behauptet wie rechts oben, deutete in der Hälfte der Fälle falsch
    herum.

    **Direkt auf den Knoten geschrieben, kein State.** Dieselbe Begründung wie
    beim **Drag Handle** (`CanvasDragHandle`): Der Wert ändert sich bei jeder
    Zeigerbewegung, und über State zöge das ein Rendern des Streifens in jedem
    Frame nach sich — samt seiner beiden Lagen und der `data:`-URL seines
    Bildes. `style.setProperty` kostet nichts.

    Und deshalb steht die Variable NICHT im `style`-Objekt unten: React setzte
    sie bei jedem Rendern auf ihren dortigen Wert zurück, und die Hand hätte
    für einen Frame den falschen Cursor.

    **Während der Drehung steht er still.** Der Zeiger ist aufgenommen und
    zieht weite Kreise um den Streifen; ein Cursor, der dabei durch alle vier
    Ecken flackerte, erzählte von einer Wahl, die längst getroffen ist.
  */
  const zeigeCursor = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const el = rootRef.current
    if (!el || !stellbar || rotating) return
    const box = el.getBoundingClientRect()
    el.style.setProperty('--canvas-rotate-cursor', canvasRotateCursorAt(
      { x: box.left + box.width / 2, y: box.top + box.height / 2 },
      { x: e.clientX, y: e.clientY },
    ))
  }, [stellbar, rotating])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    zeigeCursor(e)
    rotateMove(e)
  }, [zeigeCursor, rotateMove])

  /*
    Der Weg ohne Zeiger: **Tab** hält am Streifen an, dort tut jede Pfeiltaste
    eine Raste — **Pfeil runter die nächste, Pfeil hoch die vorige.**

    Das ist seit September 2026 eine Verabredung und kein Abbild des Zugs mehr:
    Wer den Streifen im Kreis führt, greift ihn irgendwo auf seiner Fläche, und
    links vom Drehpunkt bedeutet „nach unten" das Gegenteil von rechts davon.
    Eine Taste kann das nicht spiegeln (siehe `stepCanvasTapeAngle`).

    `preventDefault`, damit React Flow den Node nicht nebenher verschiebt (seine
    Tastatur-Bedienung ist nicht abgeschaltet).
  */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!stellbar || !look) return
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    e.stopPropagation()
    const ziel = stepCanvasTapeAngle(look.spot, look.angle, e.key === 'ArrowDown' ? 1 : -1)
    if (ziel !== look.angle) onAngle?.(noteId, ziel)
  }, [stellbar, look, noteId, onAngle])

  const handleClick = useCallback(() => {
    // Ein Zug meldet sich hier ab: Er hat die Neigung gestellt, sonst nichts.
    if (consumeDragged()) return
    if (disabled) return
    onClick(noteId)
  }, [consumeDragged, onClick, noteId, disabled])

  if (!look || !shape) return null

  const { spot } = look
  // Die Vorschau, solange eine läuft — zurückgedreht in die Zählung der Zeile.
  const angle = rotate.preview !== null ? -rotate.preview : look.angle

  return (
    <WithTooltip content={t('projectCanvas.note.tapeHandle')}>
      <button
        ref={rootRef}
        type="button"
        className={
          `canvas-tape canvas-tape--${spot} nodrag nopan`
          + (rollingOn ? ' canvas-tape--rolling-on' : '')
          + (tapped ? ' canvas-tape--tapped' : '')
          + (peeling ? ' canvas-tape--peeling' : '')
        }
        // Ohne Prüfung auf den Namen: Solange `rollingOn` steht, ist das
        // Aufrollen die einzige Bewegung auf diesem Element — das Abziehen kommt
        // erst viel später und findet die Klasse längst weg.
        onAnimationEnd={() => setRollingOn(false)}
        style={{
          // Maße und Winkel sind DATEN (sie kommen aus Figma und aus der
          // Kennung), nicht Gestaltung — deshalb als Custom Properties hier und
          // das Rezept in der SCSS. Dieselbe Aufteilung wie bei der Fläche des
          // Zettels (`--note-fill`).
          '--tape-image': `url("${shape.url}")`,
          '--tape-width': `${shape.width}px`,
          '--tape-height': `${shape.height}px`,
          '--tape-angle': `${angle}deg`,
          /*
            **Der Dreh-Cursor steht NICHT hier** — er wird beim Überfahren direkt
            auf den Knoten geschrieben (siehe `zeigeCursor` oben), weil er von der
            Lage des Zeigers abhängt. Stünde er in diesem Objekt, setzte React ihn
            bei jedem Rendern zurück.

            Was hier fehlt, fängt die SCSS ab: Sie liest
            `var(--canvas-rotate-cursor, alias)`, und `alias` ist auf beiden
            Systemen ein gebogener Pfeil — die richtige Ansage, bevor der Zeiger
            das erste Mal über dem Streifen war.
          */
        } as React.CSSProperties}
        aria-label={t('projectCanvas.note.untape')}
        disabled={disabled}
        onClick={handleClick}
        {...rotateHandlers}
        onPointerEnter={zeigeCursor}
        onPointerMove={handlePointerMove}
        onKeyDown={handleKeyDown}
      >
        <span className="canvas-tape__laid" aria-hidden="true" />
        <span className="canvas-tape__roll" aria-hidden="true" />
      </button>
    </WithTooltip>
  )
}
